import React, { useState } from 'react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ArrowRight, CheckCircle, ShoppingCart, Target, Star } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const AuthorsLandingPage = () => {
  const navigate = useNavigate();
  const [purchasingId, setPurchasingId] = useState(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handlePurchase = async (packageId) => {
    setPurchasingId(packageId);
    try {
      const res = await fetch(`${API_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin })
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch {
      alert('Unable to process payment. Please try again.');
    } finally {
      setPurchasingId(null);
    }
  };

  const guides = [
    { id: 'facebook_ecosystem', title: 'Facebook eCoSystem', image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/rnk6gz3y_THE%20Ultimate%20Authors%20FACEbook%20cover.png' },
    { id: 'instagram_ecosystem', title: 'Instagram eCoSystem', image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png' },
    { id: 'tiktok_ecosystem', title: 'TikTok eCoSystem', image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/g4dpspzt_Ultimate%20Author%20TIKTOK%20eCoSystem%20front%20cover.png' },
    { id: 'website_ecosystem', title: 'Author Resource Guide', image: 'https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/l3735rry_AUTHOR%20RESOURCE%20GUIDE%20-%20FINAL.png' },
  ];

  const bundleFeatures = [
    '400+ verified marketing platforms',
    'Facebook, Instagram, TikTok guides',
    'Author Resource Guide',
    'Case Study Blueprint (FREE bonus)',
    'Lifetime access to all content',
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-green-900/30 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            <div>
              <div className="inline-flex items-center gap-2 text-green-400 text-sm font-semibold mb-4">
                <BookOpen className="w-4 h-4" />
                FOR AUTHORS
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4" data-testid="authors-title">
                The Ultimate Author <span className="text-green-400">eCoSystem</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed mb-6">
                Stop wasting hours searching for where to promote your book. Get 400+ verified platforms where MILLIONS of readers are actively looking for their next great read.
              </p>
              <div className="flex items-center gap-4 mb-8">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
                </div>
                <span className="text-gray-400 text-sm">Bestseller — Trusted by Authors Worldwide</span>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => handlePurchase('complete_ecosystem')} disabled={purchasingId === 'complete_ecosystem'} className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-3 text-lg">
                  {purchasingId === 'complete_ecosystem' ? 'Processing...' : 'Get Full Bundle — $97'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button onClick={() => navigate('/author-ecosystem')} variant="outline" className="border-green-600 text-green-400 hover:bg-green-600 hover:text-white">
                  Learn More
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_author-portfolio-pro/artifacts/ir99bh6p_THE%20ULTIMATE%20AUTHOR%20INSTAGRAM%20eCoSystem%20front%20cover.png"
                alt="Author eCoSystem"
                className="w-80 lg:w-96 rounded-xl shadow-2xl shadow-green-900/40"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bundle Features */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-gray-900/50 border border-green-800/30 rounded-2xl p-8 lg:p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">Complete Bundle — $97</h2>
              <p className="text-gray-400">Everything you need to market your book and grow your readership</p>
              <p className="text-green-400 font-semibold mt-2">Save $77 vs. buying individually ($174 value)</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {bundleFeatures.map((feature) => (
                <div key={feature} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-300">{feature}</span>
                </div>
              ))}
            </div>
            <div className="text-center">
              <Button onClick={() => handlePurchase('complete_ecosystem')} disabled={purchasingId === 'complete_ecosystem'} className="bg-green-600 hover:bg-green-700 text-white font-bold px-10 py-4 text-lg">
                {purchasingId === 'complete_ecosystem' ? 'Processing...' : 'Get Instant Access — $97'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Individual Guides */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-2">Or Buy Individually</h2>
            <p className="text-gray-400">Each guide available separately at $27</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {guides.map((guide) => (
              <div key={guide.id} className="bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden hover:border-green-600/40 transition-all group">
                <div className="aspect-square">
                  <img src={guide.image} alt={guide.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-bold text-white mb-2">{guide.title}</h3>
                  <div className="flex items-center justify-between">
                    <span className="text-green-400 font-bold">$27</span>
                    <Button
                      onClick={() => handlePurchase(guide.id)}
                      disabled={purchasingId === guide.id}
                      size="sm"
                      className="bg-green-600 hover:bg-green-700 text-white text-xs"
                      data-testid={`buy-${guide.id}`}
                    >
                      <ShoppingCart className="w-3 h-3 mr-1" />
                      {purchasingId === guide.id ? '...' : 'Buy'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA to Ghostwriting */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Target className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4">Need Help Writing Your Book?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">Our ghostwriting and publishing division can help bring your book from concept to bestseller.</p>
          <Button onClick={() => navigate('/start-project')} className="bg-red-600 hover:bg-red-700 text-white font-bold px-8 py-4 text-lg">
            Start a Project <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AuthorsLandingPage;
